import { useEffect, useMemo, useState } from 'react';
import { type GameId } from '../types/games';
import { useSurvey } from '@hotpot-arcade/fanforge-api-client';
import { useAnalytics } from '@hotpot-arcade/analytics-client';
import { Flags } from '@hotpot-arcade/fanforge-core';
import { useGameIds } from './use-game-definition';

function useHiddenDueToMissingSurvey(): string[] {
  const { survey } = useSurvey();
  const analytics = useAnalytics();
  const creatorSurveyEnabled = analytics?.getFeatureFlag(Flags.creatorSurvey);

  return useMemo(() => {
    return !survey || !creatorSurveyEnabled ? ['creator-survey'] : []; // start hidden, until we know survey exists
  }, [survey, creatorSurveyEnabled]);
}

function useHiddenDueToFeatureFlag(): string[] {
  const analytics = useAnalytics();
  const bubboGameEnabled = analytics?.getFeatureFlag(Flags.bubboGame);
  const caketowerGameEnabled = analytics?.getFeatureFlag(Flags.caketowerGame);
  const ffchessGameEnabled = analytics?.getFeatureFlag(Flags.ffchessGame);
  const juicejamGameEnabled = analytics?.getFeatureFlag(Flags.juicejamGame);

  return useMemo(() => {
    // show all games in dev environment
    const isDevEnvironment = import.meta.env.MODE === 'development';
    if (isDevEnvironment) return [];

    // otherwise, hide games behind feature flag
    const gameIds: string[] = [];
    if (!bubboGameEnabled) gameIds.push('bubbo');
    if (!caketowerGameEnabled) gameIds.push('caketower');
    if (!ffchessGameEnabled) gameIds.push('ffchess');
    if (!juicejamGameEnabled) gameIds.push('juicejam');
    return gameIds;
  }, [bubboGameEnabled, caketowerGameEnabled, ffchessGameEnabled, juicejamGameEnabled]);
}

export function useVisibleGameIds(): GameId[] {
  const gameIds = useGameIds();
  const [visibleGameIds, setVisibleGameIds] = useState<GameId[]>([...gameIds]);
  const hiddenDueToMissingSurvey = useHiddenDueToMissingSurvey();
  const hiddenDueToFeatureFlag = useHiddenDueToFeatureFlag();

  useEffect(() => {
    const hiddenGameIds = [...hiddenDueToMissingSurvey, ...hiddenDueToFeatureFlag];
    setVisibleGameIds(gameIds.filter((id) => !hiddenGameIds.includes(id)));
  }, [gameIds, hiddenDueToMissingSurvey, hiddenDueToFeatureFlag]);

  return visibleGameIds;
}
